import { createContext, useContext } from "react";
import { CalendarApi } from "../../api-client";
import CalendarStore from "./calendarStore";
import { AuthContext } from "../Auth/auth.provider";
import { UserContext } from "../User/User.provider";
import useStore from "../../helpers/useStore";
import { useLocalObservable } from "mobx-react";
import { useSearchParamProjection } from "../../helpers/searchParams/useSearchParamProjection";
import { createApiConfig } from "../../api/createApiConfig";

type ProviderOpts = {
  /**
   * Remove params equal to defaults from the URL to keep it clean.
   * Default: true
   */
  elideDefaults?: boolean;

  /**
   * Use history.replaceState instead of pushState when writing URL.
   * Default: true
   */
  replaceHistory?: boolean;

  /**
   * If true, will navigate to the store’s @searchRoute base path (e.g. "/patient")
   * if the current location path doesn’t match.
   * Default: false
   */
  navigateToBase?: boolean;

  /**
   * Debounce URL writes in milliseconds.
   * Useful if you’re syncing e.g. a search query on every keystroke.
   * Default: 0 (no debounce)
   */
  debounceMs?: number;
};

export const CalendarContext = createContext<CalendarStore | null>(null);
CalendarContext.displayName = "CalendarContext";

export const CalendarProvider: React.FC<React.PropsWithChildren<ProviderOpts>> = ({
  children,
  elideDefaults = true,
  replaceHistory = true,
  navigateToBase = false,
  debounceMs = 120,
}) => {
  const AuthenticationStore = useStore(AuthContext);

  const config = createApiConfig();

  const apiClient = new CalendarApi(config);
  const store = useLocalObservable(() => new CalendarStore(apiClient, AuthenticationStore));

  // Keep the store's apiClient reference in sync when the provider re-renders
  store.apiUnwrapped = apiClient;

  // MD-286: mirror the logged-in user's current location into the store so
  // slot-creation payloads can be stamped with the site they are planned at.
  // UserContext may be absent in isolated tests; the stamp then stays null and
  // the backend stores a location-less slot (visible at every site).
  const userStore = useContext(UserContext);
  store.currentLocationId = userStore?.user?.locationId ?? null;

  // Project calendar store state to URL for refresh persistence (MobX is source of truth)
  useSearchParamProjection(store, CalendarStore, {
    elideDefaults,
    replaceHistory,
    navigateToBase,
    debounceMs,
    restrictToBase: true,
  });

  return <CalendarContext.Provider value={store}>{children}</CalendarContext.Provider>;
};
